import { components } from "@certn/api/generated-schemas/client-portal";
import {
  CopyToClipboard,
  DialogTrigger,
  GhostIconButton,
  HoverInfo,
  Icon,
  Popover,
  PopoverContent,
  Text,
} from "@certn/cascadia-react";
import { getRegionKey } from "@certn/utils";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { LinkedCaseItem } from "../../../components/linked-cases/LinkedCaseItem";
import { joinNameParts } from "../utils";

function getApplicantPayIcon(): "currency-dollar" | "currency_pound" {
  const region = getRegionKey();
  return region === "uk" ? "currency_pound" : "currency-dollar";
}

type Props = {
  name?: components["schemas"]["CurrentName"];
  email?: string;
  linkedCases?: components["schemas"]["BaseCaseListLinkedCases"][];
  isMobile?: boolean;
  isApplicantPay?: boolean;
};
export function NameTableCell({
  name,
  email,
  linkedCases = [],
  isMobile,
  isApplicantPay,
}: Props) {
  const { t } = useTranslation();

  return (
    <div className={styles.container}>
      <div>
        <Text
          as="div"
          testID="applicant-full-name"
          style={isMobile ? "summary/content-primary" : "form/body-secondary"}
        >
          {joinNameParts(name) || "--"}
        </Text>
        <Text
          as="div"
          style={isMobile ? "global/body-secondary" : "global/info-label"}
        >
          {email ? (
            <CopyToClipboard text={email} testID="copyable-applicant-email" />
          ) : (
            "--"
          )}
        </Text>
      </div>
      <div className={styles.endSlot}>
        {isApplicantPay ? (
          <HoverInfo
            testID="applicant-pay-tooltip"
            trigger={
              <Icon
                name={getApplicantPayIcon()}
                type="mini"
                color="text-weak"
                size="space-5"
                testID="applicant-pay-icon"
              />
            }
            infoContent={
              <Text as="div" style="global/body-secondary">
                {t(
                  "This is an Applicant Pay case, where the applicant will be paying the cost of the checks."
                )}
              </Text>
            }
          />
        ) : null}
        {linkedCases.length > 0 && (
          <DialogTrigger>
            <GhostIconButton
              testID="co-applicants-popover-trigger"
              size="small"
              icon="group"
              aria-label={t("Co-applicant")}
            />
            <Popover placement={isMobile ? "top right" : "top"}>
              <PopoverContent testID="co-applicants-popover">
                {linkedCases.map((linkedCase, index) => {
                  return (
                    <LinkedCaseItem
                      key={linkedCase.short_id}
                      linkedCase={linkedCase}
                      linkedCaseNumber={
                        linkedCases.length > 1 ? index + 1 : undefined
                      }
                    />
                  );
                })}
              </PopoverContent>
            </Popover>
          </DialogTrigger>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "var(--space-5)",
  }),
  endSlot: css({
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
  }),
  linkedCaseContainer: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
};
